// card-sandbox: the pure host-side handler for card→host messages.
//
// Factored out of the iframe/DOM wiring because that's the part worth testing and
// the part where the trust decisions live — jsdom can't run cross-frame
// postMessage realistically, so the wiring gets live verification while THIS gets
// unit tests (mirrors how runtime.js isolated `_inject`).
//
// `ctx` carries the host's capabilities as plain callbacks so nothing here needs a
// real iframe/window:
//   ctx.send(msg)                  — post a host→card message back
//   ctx.onReady(protocol)          — card handshook; host decides compat + inits
//   ctx.onAction(action, structured) — forward a GATED action to CardFor's onAction
//   ctx.actionGate(action)         — policy: may this card emit this action_spec?
//   ctx.carriedContinue(action)    — the host's copy of a built-in Continue its payload carries, or null
//   ctx.onInvoke(reqId, action, structured) — forward a GATED request/response call to
//                                    the card's helper (v2); the host stamps identity,
//                                    dispatches, and later sends host:invoke:result
//   ctx.onResize(height)           — size the iframe
//   ctx.onSwipe(dir)               — a horizontal swipe ('next'|'prev') the host pager may page on
//   ctx.onEnd()                    — a live card says it has finished
//   ctx.storage.get(key) / .set(key, value) — host-owned, (account,kind)-scoped
//   ctx.log(reason, detail)        — dev diagnostics for dropped/denied frames

import { CARD, HOST, isValidCardMessage } from './protocol';

// Default action gate (the strict floor per the ADR): a card may emit an action
// only against its OWN helper's function-set(s). `allowedFunctionSets` is the set
// the card's package declares; anything else is denied. Broaden only when a real
// cross-helper need is designed — never by default. An action with no
// function_set is denied (can't prove ownership).
//
// v1 trust model (!380 review Finding 2): the allowlist is TRUST-ON-DECLARATION —
// it comes from the card package's own provider.yml/manifest `cards:` entry and is
// NOT yet cross-checked against the function-sets the emitting helper actually
// publishes. So this gate is a defense-in-depth floor, not the last word: the real
// authorization boundary is §6 invoke-auth + station routing, which still governs
// whether a gated action lands. Acceptable for v1 (first-party card, operator-
// authored package); a follow-up binds the allowlist to the helper's published
// sets so a package can't self-declare a broader scope. See
// backlog/card-sandbox-gate-binding.md.
export function makeActionGate(allowedFunctionSets) {
  const allowed = new Set(allowedFunctionSets || []);
  return (action) => {
    const fs = action && (action.function_set || action.functionSet);
    return typeof fs === 'string' && allowed.has(fs);
  };
}

// station-face: a face may call only the (function_set, function) pairs its owner approved,
// which the Face helper puts on the card. The BFF tile rail re-checks the same list
// server-side (face_gate), so a card that forges `invokes` gains nothing there.
export function makeFaceGate(invokes) {
  const allowed = new Set((Array.isArray(invokes) ? invokes : [])
    .filter((i) => i && typeof i.function_set === 'string' && typeof i.function === 'string')
    .map((i) => JSON.stringify([i.function_set, i.function])));
  return (action) => {
    const fs = action && (action.function_set || action.functionSet);
    const fn = action && action.function;
    return typeof fs === 'string' && typeof fn === 'string' && allowed.has(JSON.stringify([fs, fn]));
  };
}

// Handle one already-source-verified card→host message. Returns a short tag of
// what happened (for tests + logging); all real effects go through ctx callbacks.
// NEVER throws — an untrusted card must not be able to crash the host by shaping a
// bad frame.
export function handleCardMessage(msg, ctx) {
  if (!isValidCardMessage(msg)) {
    ctx.log && ctx.log('dropped:invalid', msg);
    return 'dropped';
  }
  switch (msg.type) {
    case CARD.READY:
      ctx.onReady && ctx.onReady(msg.protocol);
      return 'ready';

    case CARD.ACTION: {
      // A built-in (Continue) is navigation: allowed only as the payload carries it, and the host's
      // copy is what runs, with the host's payload.
      if (msg.action.builtin != null) {
        const own = ctx.carriedContinue ? ctx.carriedContinue(msg.action) : null;
        if (!own) {
          ctx.log && ctx.log('denied:builtin', msg.action);
          return 'action-denied';
        }
        ctx.onAction && ctx.onAction(own);
        return 'continue';
      }
      const gate = ctx.actionGate || (() => false);
      if (!gate(msg.action)) {
        ctx.log && ctx.log('denied:action', msg.action);
        return 'action-denied';
      }
      ctx.onAction && ctx.onAction(msg.action, msg.structured);
      return 'action';
    }

    case CARD.INVOKE: {
      // v2 host-mediated backend channel (request/response to the card's OWN helper).
      // Same confused-deputy floor as card:action — gate on the card's declared
      // function_sets. On deny we STILL reply on the reqId ({ok:false}) so the card's
      // pending request settles instead of hanging. The session/player identity is
      // stamped by the host in onInvoke (it holds the chat session); the pure handler
      // gates but has no identity to stamp, and the card can never supply its own.
      const gate = ctx.actionGate || (() => false);
      if (msg.action.builtin != null || !gate(msg.action)) {
        ctx.log && ctx.log('denied:invoke', msg.action);
        ctx.send && ctx.send({ type: HOST.INVOKE_RESULT, reqId: msg.reqId, ok: false });
        return 'invoke-denied';
      }
      ctx.onInvoke && ctx.onInvoke(msg.reqId, msg.action, msg.structured);
      return 'invoke';
    }

    case CARD.REVEAL:
      // Advisory, like resize and swipe: the card cannot scroll the page itself (no parent
      // DOM), so it asks. A host with nowhere to scroll simply has no onReveal wired.
      ctx.onReveal && ctx.onReveal();
      return 'reveal';

    case CARD.END:
      // A host showing no live card has no onEnd wired.
      ctx.onEnd && ctx.onEnd();
      return 'end';

    case CARD.RESIZE:
      ctx.onResize && ctx.onResize(msg.height);
      return 'resize';

    case CARD.SWIPE:
      // card-pager-swipe: advisory UI hint — forward the direction to the host. A card
      // that isn't inside a pager has no onSwipe wired, so this is simply ignored.
      ctx.onSwipe && ctx.onSwipe(msg.dir);
      return 'swipe';

    case CARD.STORAGE: {
      // Host-owned persistence: the card never touches storage directly, so it
      // can't read the parent origin's other keys (session, webpush). Reply on
      // the same reqId. Best-effort: a store failure returns {ok:false}, never
      // throws back at the card.
      let ok = true;
      let value;
      try {
        if (msg.op === 'get') {
          value = ctx.storage ? ctx.storage.get(msg.key) : undefined;
        } else {
          if (ctx.storage) ctx.storage.set(msg.key, msg.value);
        }
      } catch (e) {
        ok = false;
        ctx.log && ctx.log('storage:error', e);
      }
      ctx.send && ctx.send({ type: HOST.STORAGE_RESULT, reqId: msg.reqId, ok, value });
      return 'storage';
    }

    default:
      // isValidCardMessage already filtered unknown types; defensive only.
      ctx.log && ctx.log('dropped:unhandled', msg);
      return 'dropped';
  }
}

// Source guard for the event listener: only frames from THIS card's iframe window
// are ours. A sandboxed (opaque-origin) iframe posts with origin "null", so we
// verify by source identity, not origin string — that's the reliable check when
// the sender's origin is intentionally opaque.
export function isFromCard(event, iframeWindow) {
  return !!iframeWindow && event.source === iframeWindow;
}
